import { isClsShellMessage, postToClsShell } from "@/lib/portalMessaging";

// Polyfill localStorage and sessionStorage if blocked (e.g. inside third-party iframes)
(() => {
  const createMockStorage = (nativeStorage) => {
    let data = {};
    if (nativeStorage) {
      try {
        for (let i = 0; i < nativeStorage.length; i++) {
          const key = nativeStorage.key(i);
          data[key] = nativeStorage.getItem(key);
        }
      } catch (e) {}
    }
    return {
      getItem: (key) => (key in data ? data[key] : null),
      setItem: (key, value) => {
        data[key] = String(value);
        if (nativeStorage) {
          try { nativeStorage.setItem(key, String(value)); } catch (e) {}
        }
        try {
          postToClsShell({ type: "LOCAL_STORAGE_SET", key, value: String(value) });
        } catch (e) {}
      },
      removeItem: (key) => {
        delete data[key];
        if (nativeStorage) {
          try { nativeStorage.removeItem(key); } catch (e) {}
        }
        try {
          postToClsShell({ type: "LOCAL_STORAGE_REMOVE", key });
        } catch (e) {}
      },
      clear: () => {
        data = {};
        if (nativeStorage) {
          try { nativeStorage.clear(); } catch (e) {}
        }
        try {
          postToClsShell({ type: "LOCAL_STORAGE_CLEAR" });
        } catch (e) {}
      },
      get length() { return Object.keys(data).length; },
      key: (index) => Object.keys(data)[index] || null,
      // The real browser storage. `data` above is a copy taken when this tab loaded, so it
      // misses anything another tab has saved since; src/lib/deviceStorage.js reads this.
      __native: nativeStorage,
      __initData: (newData) => {
        data = { ...data, ...newData };
        if (nativeStorage) {
          try {
            for (const key in newData) {
              nativeStorage.setItem(key, String(newData[key]));
            }
          } catch (e) {}
        }
      }
    };
  };

  let nativeLocal = null;
  try {
    const testKey = "__storage_test__";
    window.localStorage.setItem(testKey, testKey);
    window.localStorage.removeItem(testKey);
    nativeLocal = window.localStorage;
  } catch (e) {
    console.warn("localStorage is blocked or unavailable. Using in-memory mock storage.");
  }

  const mockLocal = createMockStorage(nativeLocal);
  try {
    Object.defineProperty(window, "localStorage", {
      value: mockLocal,
      configurable: true,
      enumerable: true,
      writable: true
    });
  } catch (err) {
    console.error("Failed to redefine window.localStorage:", err);
  }

  // Handle in-memory session storage mock if needed
  const createSimpleMockStorage = () => {
    let data = {};
    return {
      getItem: (key) => (key in data ? data[key] : null),
      setItem: (key, value) => { data[key] = String(value); },
      removeItem: (key) => { delete data[key]; },
      clear: () => { data = {}; },
      get length() { return Object.keys(data).length; },
      key: (index) => Object.keys(data)[index] || null
    };
  };

  try {
    const testKey = "__storage_test__";
    window.sessionStorage.setItem(testKey, testKey);
    window.sessionStorage.removeItem(testKey);
  } catch (e) {
    console.warn("sessionStorage is blocked or unavailable. Falling back to in-memory mock.");
    try {
      Object.defineProperty(window, "sessionStorage", {
        value: createSimpleMockStorage(),
        configurable: true,
        enumerable: true,
        writable: true
      });
    } catch (err) {
      console.error("Failed to redefine window.sessionStorage:", err);
    }
  }

  // Set up message bridge receiver for loading initial storage
  window.addEventListener("message", (event) => {
    if (!isClsShellMessage(event)) return;
    if (event.data && event.data.type === "LOCAL_STORAGE_INIT") {
      const storageData = event.data.data;
      if (storageData && typeof mockLocal.__initData === "function") {
        const parsedData = {};
        for (const key in storageData) {
          parsedData[key] = storageData[key];
        }
        mockLocal.__initData(parsedData);
      }
      // Dispatch events to notify listeners that storage is populated
      window.dispatchEvent(new Event("storage"));
      window.dispatchEvent(new CustomEvent("localstorage_sync"));
    }
  });

  // Request storage load from parent window
  try {
    postToClsShell({ type: "LOCAL_STORAGE_READY" });
  } catch (e) {}
})();

// Global error debugger for catching iframe/runtime crashes
(() => {
  if (!import.meta.env.DEV) return;

  const errorBox = document.createElement("div");
  errorBox.id = "browser-debugger-panel";
  errorBox.style.cssText = "position:fixed;top:0;left:0;right:0;z-index:999999;background:#fef2f2;color:#991b1b;border-bottom:4px solid #ef4444;padding:16px;font-family:monospace;font-size:12px;display:none;max-height:50vh;overflow-y:auto;white-space:pre-wrap;";
  
  const title = document.createElement("h3");
  title.innerText = "⚠️ Browser Script Crash Detected";
  title.style.margin = "0 0 8px 0";
  title.style.fontSize = "14px";
  errorBox.appendChild(title);

  const errorText = document.createElement("div");
  errorText.id = "browser-debugger-text";
  errorBox.appendChild(errorText);

  // Append immediately if body is ready, or wait for DOMContentLoaded
  const appendBox = () => {
    if (document.body) {
      document.body.appendChild(errorBox);
    } else {
      setTimeout(appendBox, 50);
    }
  };
  appendBox();

  const showError = (message, source, lineno, colno, error) => {
    errorBox.style.display = "block";
    const stack = error && error.stack ? error.stack : "No stack trace available";
    errorText.innerText = `Error: ${message}\nSource: ${source}:${lineno}:${colno}\n\nStack:\n${stack}`;
  };

  window.onerror = (message, source, lineno, colno, error) => {
    if (message === "Script error." && !source && Number(lineno) === 0 && Number(colno) === 0) {
      return false;
    }
    showError(message, source, lineno, colno, error);
    return false;
  };

  window.onunhandledrejection = (event) => {
    errorBox.style.display = "block";
    const reason = event.reason;
    const stack = reason && reason.stack ? reason.stack : "No stack trace available";
    errorText.innerText = `Unhandled Promise Rejection: ${reason}\n\nStack:\n${stack}`;
  };
})();

import React from 'react'
import ReactDOM from 'react-dom/client'
import App from '@/App.jsx'
import '@/index.css'

ReactDOM.createRoot(document.getElementById('root')).render(
  <App />
)
